import { logger } from '../log';
import type { OptionCallPut } from '../types';
import type { SymbolPickerAction, FocusMode } from './actions';
import type { InstrumentType, FocusedElement, SymbolPickerState, DropdownState } from './types';
import {
    getFilterFunction,
    getSortFunction,
    getNextElement,
    validateStateInstrumentAndProduceSymbolString,
    getElementDropdown,
    createInitialState,
    findClosestStrikePriceIndex,
} from './utils';

const log = logger.getSubLogger({ name: 'symbolPickerState' });

const createInitialDropdownState = (allItems: any[] = []): DropdownState => ({
    allItems,
    state: 'ready',
    activeIndex: 0,
    searchTerm: '',
    filteredItems: allItems,
});

export const initialState: SymbolPickerState = {
    focusedElement: null,
    openDropdown: null,
    allowedInstrumentTypes: [],
    symbolString: '',
    isUserModified: false,
    underlyingSymbolPrices: {
        bidPrice: undefined,
        askPrice: undefined,
        lastPrice: undefined,
    },
    instrument: {
        instrumentType: 'equity',
        underlyingSymbol: '',
        expirationDate: undefined,
        optionCallPut: undefined,
        strikePrice: undefined,
    },
    dropdowns: {
        instrumentType: createInitialDropdownState([]),
        underlyingSymbol: createInitialDropdownState(),
        expirationDate: createInitialDropdownState(),
        optionCallPut: createInitialDropdownState(['C', 'P']),
        strikePrice: createInitialDropdownState(),
    },
};

// Helper function to clear dependent fields when a parent field changes
// Only clears the value, preserving dropdown state (allItems, state)
function clearDependentFields(
    changedField: FocusedElement,
    instrumentType: InstrumentType,
    currentState: SymbolPickerState,
): Partial<SymbolPickerState> {
    const updates: Partial<SymbolPickerState> = {};

    switch (changedField) {
        case 'instrumentType':
            // When instrument type changes, clear most option-specific fields
            updates.instrument = {
                ...currentState.instrument,
                underlyingSymbol: currentState.instrument.underlyingSymbol, // Preserve current value
                expirationDate: undefined,
                optionCallPut: undefined,
                strikePrice: undefined,
            };
            break;

        case 'underlyingSymbol':
            // When underlying symbol changes in options mode, clear all dependent fields
            if (instrumentType === 'options') {
                updates.instrument = {
                    ...currentState.instrument,
                    expirationDate: undefined,
                    optionCallPut: undefined,
                    strikePrice: undefined,
                };
            }
            break;

        case 'expirationDate':
            // When expiration date changes, clear option type and strike price
            updates.instrument = {
                ...currentState.instrument,
                optionCallPut: undefined,
                strikePrice: undefined,
            };
            break;

        case 'optionCallPut':
            // When option type changes, clear strike price
            updates.instrument = {
                ...currentState.instrument,
                strikePrice: undefined,
            };
            break;

        case 'strikePrice':
            // Strike price is the last field, no dependents to clear
            break;
    }

    return updates;
}

// Helper function to update element dropdown state
function updateElementDropdown(
    state: SymbolPickerState,
    element: FocusedElement,
    updates: Partial<DropdownState>,
): Partial<SymbolPickerState> {
    return {
        dropdowns: {
            ...state.dropdowns,
            [element]: {
                ...state.dropdowns[element],
                ...updates,
            },
        },
    };
}

// Generic helper function to update element state with type-safe overloads
function updateElementState(
    element: 'instrumentType',
    currentState: SymbolPickerState,
    newValue: InstrumentType,
    focus?: FocusMode,
    closeDropdown?: boolean,
): Partial<SymbolPickerState>;
function updateElementState(
    element: 'underlyingSymbol',
    currentState: SymbolPickerState,
    newValue: string,
    focus?: FocusMode,
    closeDropdown?: boolean,
): Partial<SymbolPickerState>;
function updateElementState(
    element: 'expirationDate',
    currentState: SymbolPickerState,
    newValue: string,
    focus?: FocusMode,
    closeDropdown?: boolean,
): Partial<SymbolPickerState>;
function updateElementState(
    element: 'optionCallPut',
    currentState: SymbolPickerState,
    newValue: OptionCallPut | undefined,
    focus?: FocusMode,
    closeDropdown?: boolean,
): Partial<SymbolPickerState>;
function updateElementState(
    element: 'strikePrice',
    currentState: SymbolPickerState,
    newValue: number | undefined,
    focus?: FocusMode,
    closeDropdown?: boolean,
): Partial<SymbolPickerState>;
function updateElementState(
    element: FocusedElement,
    currentState: SymbolPickerState,
    newValue: any,
    focus?: FocusMode,
    closeDropdown?: boolean,
): Partial<SymbolPickerState> {
    // Clear dependent fields first
    const dependentFieldUpdates = clearDependentFields(element, currentState.instrument.instrumentType, currentState);

    // Reset focused element's dropdown state when setting a value
    const elementDropdown = getElementDropdown(currentState, element);
    const sortFunction = getSortFunction(element);
    const sortedItems = sortFunction ? sortFunction(elementDropdown.allItems) : elementDropdown.allItems;

    const focusedElementResetUpdates = updateElementDropdown(currentState, element, {
        activeIndex: 0,
        searchTerm: '',
        filteredItems: sortedItems,
    });

    // Determine the next focus based on the focus mode
    let nextFocus: FocusedElement | null = currentState.focusedElement;

    if (focus === 'forward' || focus === 'backward') {
        nextFocus = getNextElement(element, currentState.instrument.instrumentType, focus);
    } else if (focus === 'focus') {
        nextFocus = element;
    }

    // Process the value based on element type
    let processedValue = newValue;
    if (element === 'underlyingSymbol' && typeof newValue === 'string') {
        processedValue = newValue;
    }

    // Build the element-specific state update
    const elementStateUpdate: Partial<SymbolPickerState> = {
        instrument: {
            ...currentState.instrument,
            ...dependentFieldUpdates.instrument,
            [element]: processedValue,
        },
    };

    return {
        ...focusedElementResetUpdates,
        ...elementStateUpdate,
        focusedElement: nextFocus,
        openDropdown: closeDropdown ? null : nextFocus,
    };
}

export function symbolPickerReducer(state: SymbolPickerState, action: SymbolPickerAction): SymbolPickerState {
    if (TRACE) {
        log.trace({ message: `symbolPickerReducer action ${action.type}`, action });
    }

    switch (action.type) {
        case 'SET_FOCUS': {
            // When focus changes, reset the dropdown UI state for that element
            const elementDropdown = getElementDropdown(state, action.element);
            const filterFunction = getFilterFunction(action.element);
            const sortFunction = getSortFunction(action.element);

            const sortedItems = sortFunction ? sortFunction(elementDropdown.allItems) : elementDropdown.allItems;
            const filteredItems = filterFunction ? filterFunction(sortedItems, '') : sortedItems;

            let activeIndex = 0;

            // If focusing strike prices and we have an underlying symbol price, set active index to closest strike price
            if (
                action.element === 'strikePrice' &&
                state.underlyingSymbolPrices.lastPrice !== undefined &&
                filteredItems.length > 0
            ) {
                activeIndex = findClosestStrikePriceIndex(filteredItems, state.underlyingSymbolPrices.lastPrice);
            }

            const elementUpdates = updateElementDropdown(state, action.element, {
                searchTerm: '',
                filteredItems,
                activeIndex,
            });

            return {
                ...state,
                ...elementUpdates,
                focusedElement: action.element,
                openDropdown: action.element,
            };
        }

        case 'LOST_FOCUS': {
            return {
                ...state,
                focusedElement: null,
                openDropdown: null,
            };
        }

        case 'SET_INSTRUMENT_TYPE': {
            const updatedState = {
                ...state,
                ...updateElementState('instrumentType', state, action.value, action.focus),
            };

            let newState = {
                ...updatedState,
                underlyingSymbolPrices: {
                    bidPrice: undefined,
                    askPrice: undefined,
                    lastPrice: undefined,
                },
                isUserModified: true,
                symbolString: validateStateInstrumentAndProduceSymbolString(updatedState.instrument),
            };

            // When we change the type of the instrument we should make sure the new type is actually allowed
            // If the current allowed types list is having just a single value we swap that value,
            // otherwise we add the new type to the list
            if (!newState.allowedInstrumentTypes.includes(action.value)) {
                const updatedAllowed =
                    newState.allowedInstrumentTypes.length === 1
                        ? [action.value]
                        : [...newState.allowedInstrumentTypes, action.value];
                newState = { ...newState, allowedInstrumentTypes: updatedAllowed };
            }

            return newState;
        }

        case 'SET_UNDERLYING_SYMBOL': {
            const updatedState = {
                ...state,
                ...updateElementState('underlyingSymbol', state, action.value, action.focus),
            };

            // Only clear underlyingSymbolPrices if the underlying symbol actually changed
            const shouldClearPrice = state.instrument.underlyingSymbol !== action.value;

            const newState = {
                ...updatedState,
                underlyingSymbolPrices: shouldClearPrice
                    ? {
                          bidPrice: undefined,
                          askPrice: undefined,
                          lastPrice: undefined,
                      }
                    : state.underlyingSymbolPrices,
                isUserModified: true,
                symbolString: validateStateInstrumentAndProduceSymbolString(updatedState.instrument),
            };

            return newState;
        }

        case 'SET_EXPIRATION_DATE': {
            const updatedState = {
                ...state,
                ...updateElementState('expirationDate', state, action.value, action.focus),
            };

            const newState = {
                ...updatedState,
                isUserModified: true,
                symbolString: validateStateInstrumentAndProduceSymbolString(updatedState.instrument),
            };

            return newState;
        }

        case 'SET_OPTION_CALL_PUT': {
            const updatedState = {
                ...state,
                ...updateElementState('optionCallPut', state, action.value, action.focus),
            };

            const newState = {
                ...updatedState,
                isUserModified: true,
                symbolString: validateStateInstrumentAndProduceSymbolString(updatedState.instrument),
            };

            return newState;
        }

        case 'SET_STRIKE_PRICE': {
            const updatedState = {
                ...state,
                ...updateElementState('strikePrice', state, action.value, action.focus, true),
            };

            const newState = {
                ...updatedState,
                isUserModified: true,
                symbolString: validateStateInstrumentAndProduceSymbolString(updatedState.instrument),
            };

            return newState;
        }

        case 'SET_DROPDOWN_ACTIVE_INDEX': {
            // Update the active index for the specified element
            const elementUpdates = updateElementDropdown(state, action.element, {
                activeIndex: action.index,
            });

            return {
                ...state,
                ...elementUpdates,
            };
        }

        case 'SET_DROPDOWN_SEARCH_TERM': {
            // Update the search term and filtered items for the specified element
            const elementDropdown = getElementDropdown(state, action.element);
            const filterFunction = getFilterFunction(action.element);
            const sortFunction = getSortFunction(action.element);

            const sortedItems = sortFunction ? sortFunction(elementDropdown.allItems) : elementDropdown.allItems;
            const filteredItems = filterFunction ? filterFunction(sortedItems, action.term) : sortedItems;

            const elementUpdates = updateElementDropdown(state, action.element, {
                searchTerm: action.term,
                filteredItems,
                activeIndex: 0, // Reset active index when filtering
            });

            return {
                ...state,
                focusedElement: action.element,
                openDropdown: action.element,
                ...elementUpdates,
            };
        }

        case 'SET_DROPDOWN_ITEMS': {
            // Update the specified element's allItems and state
            const elementDropdown = getElementDropdown(state, action.element);
            const filterFunction = getFilterFunction(action.element);
            const sortFunction = getSortFunction(action.element);

            const sortedItems = sortFunction ? sortFunction(action.items) : action.items;
            const filteredItems = filterFunction
                ? filterFunction(sortedItems, elementDropdown.searchTerm)
                : sortedItems;

            let activeIndex = 0; // Default to 0

            // If setting strike prices and we have an underlying symbol price, set active index to closest strike price
            if (
                action.element === 'strikePrice' &&
                state.underlyingSymbolPrices.lastPrice !== undefined &&
                filteredItems.length > 0
            ) {
                activeIndex = findClosestStrikePriceIndex(filteredItems, state.underlyingSymbolPrices.lastPrice);
            }

            const elementUpdates = updateElementDropdown(state, action.element, {
                allItems: sortedItems,
                state: action.loading ? 'loading' : 'ready',
                filteredItems,
                activeIndex,
            });

            return {
                ...state,
                ...elementUpdates,
            };
        }

        case 'NAVIGATE_DROPDOWN': {
            // Navigate within the specified element's dropdown
            const elementDropdown = getElementDropdown(state, action.element);

            // Only navigate if there are items
            if (elementDropdown.filteredItems.length === 0) {
                return state;
            }

            const currentIndex = elementDropdown.activeIndex;
            const maxIndex = elementDropdown.filteredItems.length - 1;

            let newIndex: number;
            if (action.direction === 'down') {
                newIndex = currentIndex >= maxIndex ? 0 : currentIndex + 1;
            } else {
                newIndex = currentIndex <= 0 ? maxIndex : currentIndex - 1;
            }

            const elementUpdates = updateElementDropdown(state, action.element, {
                activeIndex: newIndex,
            });

            return {
                ...state,
                ...elementUpdates,
            };
        }

        case 'PROVIDED_VALUE_CHANGED':
        case 'ON_PASTED_VALUE': {
            // if the new instrumentString is the same as our internal state, don't update
            // updating just wipes out our dropdown states, and serves no purpose, since our internal instrument is already in sync
            if (validateStateInstrumentAndProduceSymbolString(state.instrument) === action.payload.instrumentString) {
                return state;
            }

            const newState = createInitialState(action.payload.instrumentString, state.allowedInstrumentTypes);

            if (action.type === 'PROVIDED_VALUE_CHANGED') {
                return {
                    ...newState,
                    isUserModified: false,
                };
            }

            if (action.type === 'ON_PASTED_VALUE') {
                return {
                    ...newState,
                    isUserModified: true,
                    focusedElement: 'underlyingSymbol',
                };
            }
        }

        case 'RESET_INSTRUMENT': {
            return {
                ...state,
                instrument: {
                    strikePrice: undefined,
                    expirationDate: undefined,
                    instrumentType: action.payload.instrumentType,
                    underlyingSymbol: action.payload.underlyingSymbol,
                    optionCallPut: undefined,
                },
            };
        }

        case 'UPDATE_UNDERLYING_SYMBOL_PRICE': {
            const newState = {
                ...state,
                underlyingSymbolPrices: {
                    bidPrice: action.bidPrice,
                    askPrice: action.askPrice,
                    lastPrice: action.lastPrice,
                },
            };

            // If we have strike prices and are in options mode, set active index to closest strike price
            const strikePriceDropdown = getElementDropdown(newState, 'strikePrice');
            if (
                newState.instrument.instrumentType === 'options' &&
                strikePriceDropdown.filteredItems.length > 0 &&
                action.lastPrice !== undefined
            ) {
                const activeIndex = findClosestStrikePriceIndex(strikePriceDropdown.filteredItems, action.lastPrice);
                const elementUpdates = updateElementDropdown(newState, 'strikePrice', {
                    activeIndex,
                });

                return {
                    ...newState,
                    ...elementUpdates,
                };
            }

            return newState;
        }

        default:
            return state;
    }
}
